import { useEffect, useMemo, useState } from "react";
import researchFallback from "../../data/publicaciones.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc } from "../cmsApi.jsx";
import { MoveButtons, moveListItem } from "../components/MoveListControls.jsx";

export default function CmsResearchEditor() {
  const pubFallback = useMemo(
    () => ({
      items: researchFallback.investigacionCientifica.publicaciones || [],
    }),
    []
  );
  const directorFallback = useMemo(
    () => researchFallback.investigacionCientifica.director,
    []
  );
  const investigatorsFallback = useMemo(
    () => ({
      items: [
        {
          id: "director",
          nombre: researchFallback.investigacionCientifica.director.nombre,
          cargo: researchFallback.investigacionCientifica.director.cargo,
          titulos: researchFallback.investigacionCientifica.director.titulos || [],
          afiliacion: researchFallback.investigacionCientifica.director.afiliacion || "",
        },
      ],
    }),
    []
  );

  const pubsHook = useCmsDoc("research", "publications", pubFallback);
  const directorHook = useCmsDoc("research", "director", directorFallback);
  const investigatorsHook = useCmsDoc("research", "investigators", investigatorsFallback);

  const [pubs, setPubs] = useState(null);
  const [director, setDirector] = useState(null);
  const [investigators, setInvestigators] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (pubsHook.data) setPubs(structuredClone(pubsHook.data));
  }, [pubsHook.data]);
  useEffect(() => {
    if (directorHook.data) setDirector(structuredClone(directorHook.data));
  }, [directorHook.data]);
  useEffect(() => {
    if (investigatorsHook.data) setInvestigators(structuredClone(investigatorsHook.data));
  }, [investigatorsHook.data]);

  if (pubsHook.loading || directorHook.loading || investigatorsHook.loading || !pubs || !director || !investigators) {
    return <p className="text-sm text-brand-dark/50">Cargando Investigación…</p>;
  }

  const strip = (obj) => {
    const { id, updatedAt, updatedBy, ...rest } = obj;
    return rest;
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await Promise.all([
        saveCmsDoc("research", "publications", strip(pubs)),
        saveCmsDoc("research", "director", strip(director)),
        saveCmsDoc("research", "investigators", strip(investigators)),
      ]);
      setMessage("Investigación guardada correctamente.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h2 className="font-display text-3xl font-black">Investigación</h2>
        <p className="text-sm text-brand-dark/55 mt-1">Fuente: {pubsHook.source}</p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Director</h3>
        <Field label="Nombre">
          <input className={inputClass} value={director.nombre || ""} onChange={(e) => setDirector({ ...director, nombre: e.target.value })} />
        </Field>
        <Field label="Cargo">
          <input className={inputClass} value={director.cargo || ""} onChange={(e) => setDirector({ ...director, cargo: e.target.value })} />
        </Field>
        <Field label="Afiliación">
          <textarea className={textareaClass} value={director.afiliacion || ""} onChange={(e) => setDirector({ ...director, afiliacion: e.target.value })} />
        </Field>
        <Field label="Títulos (uno por línea)">
          <textarea
            className={textareaClass}
            value={(director.titulos || []).join("\n")}
            onChange={(e) =>
              setDirector({
                ...director,
                titulos: e.target.value.split("\n").map((t) => t.trim()).filter(Boolean),
              })
            }
          />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex justify-between">
          <h3 className="font-bold text-lg">Investigadores</h3>
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            onClick={() =>
              setInvestigators((prev) => ({
                ...prev,
                items: [
                  ...(prev.items || []),
                  { id: `inv-${Date.now()}`, nombre: "", cargo: "", titulos: [], afiliacion: "" },
                ],
              }))
            }
          >
            + Investigador
          </button>
        </div>
        {(investigators.items || []).map((inv, idx) => (
          <div key={inv.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase text-brand-dark/40">#{idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={(investigators.items || []).length}
                  onMove={(dir) =>
                    setInvestigators((prev) => ({
                      ...prev,
                      items: moveListItem(prev.items || [], idx, dir),
                    }))
                  }
                />
              </div>
              <button
                type="button"
                className="text-xs text-red-600 font-bold"
                onClick={() =>
                  setInvestigators((prev) => ({
                    ...prev,
                    items: prev.items.filter((_, i) => i !== idx),
                  }))
                }
              >
                Quitar
              </button>
            </div>
            <input
              className={inputClass}
              value={inv.nombre || ""}
              placeholder="Nombre"
              onChange={(e) => {
                const items = [...investigators.items];
                items[idx] = { ...items[idx], nombre: e.target.value };
                setInvestigators({ ...investigators, items });
              }}
            />
            <input
              className={inputClass}
              value={inv.cargo || ""}
              placeholder="Cargo"
              onChange={(e) => {
                const items = [...investigators.items];
                items[idx] = { ...items[idx], cargo: e.target.value };
                setInvestigators({ ...investigators, items });
              }}
            />
            <textarea
              className={textareaClass}
              value={inv.afiliacion || ""}
              placeholder="Afiliación"
              onChange={(e) => {
                const items = [...investigators.items];
                items[idx] = { ...items[idx], afiliacion: e.target.value };
                setInvestigators({ ...investigators, items });
              }}
            />
          </div>
        ))}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex justify-between">
          <h3 className="font-bold text-lg">Publicaciones</h3>
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            onClick={() =>
              setPubs((prev) => ({
                ...prev,
                items: [
                  {
                    id: Date.now(),
                    titulo: "Nueva publicación",
                    autores: "",
                    revista: "",
                    anio: new Date().getFullYear(),
                    doi: "",
                    url: "",
                  },
                  ...(prev.items || []),
                ],
              }))
            }
          >
            + Publicación
          </button>
        </div>
        {(pubs.items || []).map((pub, idx) => (
          <div key={pub.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase text-brand-dark/40">#{idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={(pubs.items || []).length}
                  onMove={(dir) =>
                    setPubs((prev) => ({
                      ...prev,
                      items: moveListItem(prev.items || [], idx, dir),
                    }))
                  }
                />
              </div>
              <button
                type="button"
                className="text-xs text-red-600 font-bold"
                onClick={() =>
                  setPubs((prev) => ({
                    ...prev,
                    items: prev.items.filter((_, i) => i !== idx),
                  }))
                }
              >
                Quitar
              </button>
            </div>
            <input
              className={inputClass}
              value={pub.titulo || ""}
              placeholder="Título"
              onChange={(e) => {
                const items = [...pubs.items];
                items[idx] = { ...items[idx], titulo: e.target.value };
                setPubs({ ...pubs, items });
              }}
            />
            <input
              className={inputClass}
              value={pub.autores || ""}
              placeholder="Autores"
              onChange={(e) => {
                const items = [...pubs.items];
                items[idx] = { ...items[idx], autores: e.target.value };
                setPubs({ ...pubs, items });
              }}
            />
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                className={inputClass}
                value={pub.revista || ""}
                placeholder="Revista"
                onChange={(e) => {
                  const items = [...pubs.items];
                  items[idx] = { ...items[idx], revista: e.target.value };
                  setPubs({ ...pubs, items });
                }}
              />
              <input
                className={inputClass}
                value={pub.anio || ""}
                placeholder="Año"
                onChange={(e) => {
                  const items = [...pubs.items];
                  items[idx] = { ...items[idx], anio: Number(e.target.value) || e.target.value };
                  setPubs({ ...pubs, items });
                }}
              />
            </div>
            <input
              className={inputClass}
              value={pub.url || ""}
              placeholder="URL"
              onChange={(e) => {
                const items = [...pubs.items];
                items[idx] = { ...items[idx], url: e.target.value };
                setPubs({ ...pubs, items });
              }}
            />
            <input
              className={inputClass}
              value={pub.doi || ""}
              placeholder="DOI"
              onChange={(e) => {
                const items = [...pubs.items];
                items[idx] = { ...items[idx], doi: e.target.value };
                setPubs({ ...pubs, items });
              }}
            />
          </div>
        ))}
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
